import { useMutation } from "@apollo/client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { gql } from "@/gql";
import type { DeleteHolidaysMutation } from "@/gql/graphql";
import { CREATE_HOLIDAY, DELETE_HOLIDAY, UPDATE_HOLIDAY } from "@/graphql/holidays";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useSalonStore } from "@/lib/store/salon";
import { useHolidayUIStore } from "../store/use-holiday-ui-store";
import { type Holiday, type HolidayFormData, HolidayScope, type HolidayTarget } from "../types";

const MAX_HOLIDAY_IDS_PER_DELETE = 100;

const DELETE_HOLIDAYS = gql(`
  mutation DeleteHolidays($holidayIds: [ID!]!) {
    deleteHolidays(holidayIds: $holidayIds) {
      results {
        holidayId
        status
      }
    }
  }
`);

export function useCreateHoliday() {
  const [createMutation] = useMutation(CREATE_HOLIDAY);
  const { currentSalon } = useSalonStore();

  return useAsyncAction(
    async (input: HolidayFormData, target?: HolidayTarget) => {
      if (!currentSalon?.id) {
        throw new Error("No salon selected");
      }

      const result = await createMutation({
        variables: {
          input: {
            name: input.name,
            message: input.message || null,
            startDateTime: input.startDateTime.toISO() ?? "",
            endDateTime: input.endDateTime.toISO() ?? "",
            blockOnlineBooking: input.blockOnlineBooking,
            scope: target?.scope ?? HolidayScope.SALON_SPECIFIC,
            salonIds: target?.salonIds ?? [currentSalon.id],
          },
        },
        refetchQueries: ["GetHolidays"],
      });
      return result.data?.createHoliday;
    },
    {
      loadingMessage: "Creating holiday...",
      successMessage: "Holiday created successfully!",
      errorMessage: "Failed to create holiday",
    }
  );
}

export function useUpdateHoliday() {
  const [updateMutation] = useMutation(UPDATE_HOLIDAY);
  const { currentSalon } = useSalonStore();

  return useAsyncAction(
    async (input: { id: string } & Partial<HolidayFormData>) => {
      if (!currentSalon?.id) {
        throw new Error("No salon selected");
      }

      const variables: any = {
        input: {
          id: input.id,
          ...(input.name && { name: input.name }),
          ...(input.message !== undefined && { message: input.message || null }),
          ...(input.startDateTime && { startDateTime: input.startDateTime.toISO() }),
          ...(input.endDateTime && { endDateTime: input.endDateTime.toISO() }),
          ...(input.blockOnlineBooking !== undefined && {
            blockOnlineBooking: input.blockOnlineBooking,
          }),
        },
      };

      const result = await updateMutation({
        variables,
        refetchQueries: ["GetHolidays", "GetHoliday"],
      });
      return result.data?.updateHoliday;
    },
    {
      loadingMessage: "Updating holiday...",
      successMessage: "Holiday updated successfully!",
      errorMessage: "Failed to update holiday",
    }
  );
}

export function useDeleteHoliday() {
  const [deleteMutation] = useMutation(DELETE_HOLIDAY);

  return useAsyncAction(
    async (holidayId: string) => {
      const result = await deleteMutation({
        variables: { holidayId },
        refetchQueries: ["GetHolidays"],
      });
      return result.data?.deleteHoliday;
    },
    {
      loadingMessage: "Deleting holiday...",
      successMessage: "Holiday deleted successfully!",
      errorMessage: "Failed to delete holiday",
    }
  );
}

export function useDeleteHolidays(holidays: Holiday[]) {
  const { t } = useTranslation("settings");
  const [deleteMutation] = useMutation(DELETE_HOLIDAYS);
  const [deleting, setDeleting] = useState(false);
  const { clearSelection, closeBulkDeleteDialog } = useHolidayUIStore();

  const deleteHolidays = async (holidayIds: string[]) => {
    setDeleting(true);
    try {
      const results: DeleteHolidaysMutation["deleteHolidays"]["results"] = [];
      for (let start = 0; start < holidayIds.length; start += MAX_HOLIDAY_IDS_PER_DELETE) {
        const response = await deleteMutation({
          variables: {
            holidayIds: holidayIds.slice(start, start + MAX_HOLIDAY_IDS_PER_DELETE),
          },
          refetchQueries: ["GetHolidays"],
        });
        results.push(...(response.data?.deleteHolidays.results ?? []));
      }
      const failed = results.filter((result) => result.status !== "DELETED");

      clearSelection();
      closeBulkDeleteDialog();

      if (failed.length === 0) {
        toast.success(t("holidays.bulk.toast.success", { count: results.length }));
      } else {
        const deletedCount = results.length - failed.length;
        const failures = failed
          .map((result) => {
            const name =
              holidays.find((holiday) => holiday.id === result.holidayId)?.name ?? result.holidayId;
            const reason = t(`holidays.bulk.toast.reason.${result.status}`);
            return `${name} (${reason})`;
          })
          .join(", ");
        toast.warning(t("holidays.bulk.toast.partialFailure", { count: deletedCount, failures }));
      }

      return results;
    } catch (error) {
      toast.error(t("holidays.bulk.toast.error"));
      throw error;
    } finally {
      setDeleting(false);
    }
  };

  return { deleteHolidays, deleting };
}
